import { addDays, format, parseISO } from 'date-fns'
import { z } from 'zod'

import {
  PolicyAcceptanceMethod,
  PolicyEquipmentScope,
  PolicyRevisionStatus,
  PolicySignerRule,
} from '@hapana/shared/schemas/policy-compliance/policy-compliance-enums'

import type {
  GatingSitePolicy,
  GatingSitePolicyInput,
  SitePolicyPublishResult,
  SitePolicyRevisionRow,
} from '_services/policy-compliance.service'

export const POLICY_GATING_I18N = 'policyCompliance.settings'

export const POLICY_GATING_PERMISSION = 'settings__location__policy_gating'
export const POLICY_IMPORT_PERMISSION = 'settings__location__policy_import'

export const canOpenPolicyImport = (options: {
  isMigrationEnabled: boolean
  hasImportPermission: boolean
}): boolean => options.isMigrationEnabled && options.hasImportPermission

export const SELECTABLE_POLICY_SCOPES = [
  PolicyEquipmentScope.UV,
  PolicyEquipmentScope.RED_LIGHT,
  PolicyEquipmentScope.SPRAY,
  PolicyEquipmentScope.SPA,
  PolicyEquipmentScope.WELLNESS,
] as const

export const PolicyScopeMode = {
  ALL: 'ALL',
  SET: 'SET',
} as const
export type PolicyScopeMode = (typeof PolicyScopeMode)[keyof typeof PolicyScopeMode]

export const PublishTiming = {
  NOW: 'NOW',
  SCHEDULED: 'SCHEDULED',
} as const
export type PublishTiming = (typeof PublishTiming)[keyof typeof PublishTiming]

export const RENEWAL_NEVER = 'NEVER'
export const RENEWAL_MONTH_OPTIONS: readonly string[] = [RENEWAL_NEVER, '6', '12', '24']

export const toRenewalValue = (renewalMonths: number | null | undefined): string =>
  renewalMonths ? String(renewalMonths) : RENEWAL_NEVER

// A policy saved through the API or an import can renew on any month count, so its own value is
// offered alongside the presets rather than shown as Never and cleared on the next save.
export const listRenewalOptions = (current: string): string[] =>
  RENEWAL_MONTH_OPTIONS.includes(current)
    ? [...RENEWAL_MONTH_OPTIONS]
    : [...RENEWAL_MONTH_OPTIONS, current]

export const POLICY_NAME_MAX_LENGTH = 100

export const GATING_POLICY_FIELDS = {
  NAME: 'name',
  CONTENT: 'content',
  ACCEPTANCE_METHOD: 'acceptanceMethod',
  SIGNER_RULE: 'signerRule',
  GUARDIAN_IN_PERSON_ONLY: 'guardianInPersonOnly',
  REQUIRED_FOR_EQUIPMENT_USE: 'requiredForEquipmentUse',
  SCOPE_MODE: 'scopeMode',
  SCOPES: 'scopes',
  IS_REQUIRED_TO_AGREE: 'isRequiredToAgree',
  RENEWAL: 'renewal',
  REQUIRE_REACCEPTANCE: 'requireReacceptance',
  TIMING: 'timing',
  EFFECTIVE_FROM: 'effectiveFrom',
} as const

export const GatingPolicyFormError = {
  NAME_REQUIRED: 'NAME_REQUIRED',
  NAME_TOO_LONG: 'NAME_TOO_LONG',
  CONTENT_REQUIRED: 'CONTENT_REQUIRED',
  SCOPE_REQUIRED: 'SCOPE_REQUIRED',
  IN_PERSON_NEEDS_GUARDIAN: 'IN_PERSON_NEEDS_GUARDIAN',
  DATE_REQUIRED: 'DATE_REQUIRED',
  DATE_TOO_EARLY: 'DATE_TOO_EARLY',
} as const
export type GatingPolicyFormError =
  (typeof GatingPolicyFormError)[keyof typeof GatingPolicyFormError]

const stripHtml = (value: string): string =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim()

export const hasPolicyContent = (value: string | null | undefined): boolean =>
  stripHtml(value ?? '').length > 0

export const toIsoDateOnly = (value: string | null | undefined): string | null =>
  value ? value.slice(0, 10) : null

export const getEarliestScheduleDate = (siteToday: string): string =>
  format(addDays(parseISO(`${siteToday}T00:00:00`), 1), 'yyyy-MM-dd')

export const buildGatingPolicyFormSchema = (options: {
  earliestScheduleDate: string
  isNew: boolean
}) =>
  z
    .object({
      name: z
        .string()
        .trim()
        .min(1, GatingPolicyFormError.NAME_REQUIRED)
        .max(POLICY_NAME_MAX_LENGTH, GatingPolicyFormError.NAME_TOO_LONG),
      content: z
        .string()
        .refine(hasPolicyContent, { message: GatingPolicyFormError.CONTENT_REQUIRED }),
      acceptanceMethod: z.enum(PolicyAcceptanceMethod),
      signerRule: z.enum(PolicySignerRule),
      guardianInPersonOnly: z.boolean(),
      requiredForEquipmentUse: z.boolean(),
      scopeMode: z.enum(PolicyScopeMode),
      scopes: z.array(z.enum(PolicyEquipmentScope)),
      isRequiredToAgree: z.boolean(),
      renewal: z.union([z.literal(RENEWAL_NEVER), z.string().regex(/^[1-9]\d*$/)]),
      requireReacceptance: z.boolean(),
      timing: z.enum(PublishTiming),
      effectiveFrom: z.string().nullable(),
    })
    .superRefine((values, ctx) => {
      if (
        values.requiredForEquipmentUse &&
        values.scopeMode === PolicyScopeMode.SET &&
        values.scopes.length === 0
      ) {
        ctx.addIssue({
          code: 'custom',
          message: GatingPolicyFormError.SCOPE_REQUIRED,
          path: [GATING_POLICY_FIELDS.SCOPES],
        })
      }
      if (values.guardianInPersonOnly && values.signerRule === PolicySignerRule.CLIENT) {
        ctx.addIssue({
          code: 'custom',
          message: GatingPolicyFormError.IN_PERSON_NEEDS_GUARDIAN,
          path: [GATING_POLICY_FIELDS.GUARDIAN_IN_PERSON_ONLY],
        })
      }
      if (options.isNew || values.timing !== PublishTiming.SCHEDULED) {
        return
      }
      const date = toIsoDateOnly(values.effectiveFrom)
      if (!date) {
        ctx.addIssue({
          code: 'custom',
          message: GatingPolicyFormError.DATE_REQUIRED,
          path: [GATING_POLICY_FIELDS.EFFECTIVE_FROM],
        })
      } else if (date < options.earliestScheduleDate) {
        ctx.addIssue({
          code: 'custom',
          message: GatingPolicyFormError.DATE_TOO_EARLY,
          path: [GATING_POLICY_FIELDS.EFFECTIVE_FROM],
        })
      }
    })

export type GatingPolicyFormValues = z.infer<ReturnType<typeof buildGatingPolicyFormSchema>>

export const toGatingPolicyFormValues = (
  policy: GatingSitePolicy | null
): GatingPolicyFormValues => ({
  name: policy?.name ?? '',
  content: policy?.content ?? '',
  acceptanceMethod: policy?.acceptanceMethod ?? PolicyAcceptanceMethod.CHECKBOX,
  signerRule: policy?.signerRule ?? PolicySignerRule.CLIENT,
  guardianInPersonOnly: policy?.guardianInPersonOnly ?? false,
  requiredForEquipmentUse: policy?.requiredForEquipmentUse ?? false,
  scopeMode: policy && policy.equipmentScope.length > 0 ? PolicyScopeMode.SET : PolicyScopeMode.ALL,
  scopes: policy ? [...policy.equipmentScope] : [],
  isRequiredToAgree: policy?.isRequiredToAgree ?? true,
  renewal: toRenewalValue(policy?.renewalMonths),
  requireReacceptance: false,
  timing: PublishTiming.NOW,
  effectiveFrom: null,
})

export const toGatingPolicyInput = (values: GatingPolicyFormValues): GatingSitePolicyInput => ({
  name: values.name.trim(),
  content: values.content,
  acceptanceMethod: values.acceptanceMethod,
  signerRule: values.signerRule,
  guardianInPersonOnly:
    values.signerRule === PolicySignerRule.CLIENT ? false : values.guardianInPersonOnly,
  requiredForEquipmentUse: values.requiredForEquipmentUse,
  equipmentScope:
    values.requiredForEquipmentUse && values.scopeMode === PolicyScopeMode.SET
      ? [...values.scopes].sort()
      : [],
  isRequiredToAgree: values.requiredForEquipmentUse || values.isRequiredToAgree,
  renewalMonths: values.renewal === RENEWAL_NEVER ? null : Number(values.renewal),
})

const sameScope = (left: readonly string[], right: readonly string[]): boolean =>
  [...left].sort().join(',') === [...right].sort().join(',')

export const forcesReacceptance = (
  policy: GatingSitePolicy | null,
  values: Pick<GatingPolicyFormValues, 'acceptanceMethod' | 'signerRule'>
): boolean =>
  policy !== null &&
  (policy.acceptanceMethod !== values.acceptanceMethod || policy.signerRule !== values.signerRule)

export const hasPolicyChanges = (policy: GatingSitePolicy, input: GatingSitePolicyInput): boolean =>
  policy.name !== input.name ||
  policy.content !== input.content ||
  policy.acceptanceMethod !== input.acceptanceMethod ||
  policy.signerRule !== input.signerRule ||
  policy.guardianInPersonOnly !== input.guardianInPersonOnly ||
  policy.requiredForEquipmentUse !== input.requiredForEquipmentUse ||
  policy.isRequiredToAgree !== input.isRequiredToAgree ||
  (policy.renewalMonths ?? null) !== input.renewalMonths ||
  !sameScope(policy.equipmentScope, input.equipmentScope)

export const PublishPath = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  REPUBLISH: 'REPUBLISH',
  NOTHING: 'NOTHING',
} as const
export type PublishPath = (typeof PublishPath)[keyof typeof PublishPath]

export const resolvePublishPath = (
  policy: GatingSitePolicy | null,
  values: GatingPolicyFormValues
): PublishPath => {
  if (!policy) {
    return PublishPath.CREATE
  }
  if (hasPolicyChanges(policy, toGatingPolicyInput(values))) {
    return PublishPath.UPDATE
  }
  if (values.requireReacceptance || values.timing === PublishTiming.SCHEDULED) {
    return PublishPath.REPUBLISH
  }
  return PublishPath.NOTHING
}

export const isRevisionLive = (revision: SitePolicyRevisionRow): boolean =>
  revision.status !== PolicyRevisionStatus.CANCELLED &&
  revision.status !== PolicyRevisionStatus.HISTORICAL

export const selectRevisionTimeline = (revisions: readonly SitePolicyRevisionRow[]) => {
  const sorted = [...revisions].sort((left, right) => right.revisionNumber - left.revisionNumber)
  return {
    revisions: sorted,
    active: sorted.find((revision) => revision.status === PolicyRevisionStatus.ACTIVE) ?? null,
    scheduled:
      sorted.find((revision) => revision.status === PolicyRevisionStatus.SCHEDULED) ?? null,
  }
}

export const REVISION_STATUS_PILL: Record<PolicyRevisionStatus, string> = {
  [PolicyRevisionStatus.ACTIVE]: 'active',
  [PolicyRevisionStatus.SCHEDULED]: 'scheduled',
  [PolicyRevisionStatus.SUPERSEDED]: 'voided',
  [PolicyRevisionStatus.CANCELLED]: 'canceled',
  [PolicyRevisionStatus.HISTORICAL]: 'staged',
}

export const listScopeChoices = (
  selected: readonly PolicyEquipmentScope[]
): PolicyEquipmentScope[] =>
  selected.includes(PolicyEquipmentScope.UNCATEGORISED)
    ? [...SELECTABLE_POLICY_SCOPES, PolicyEquipmentScope.UNCATEGORISED]
    : [...SELECTABLE_POLICY_SCOPES]

export const RevisionSignatures = {
  CARRY_OVER: 'CARRY_OVER',
  SIGN_AGAIN: 'SIGN_AGAIN',
} as const
export type RevisionSignatures = (typeof RevisionSignatures)[keyof typeof RevisionSignatures]

export const describeRevisionSignatures = (
  revisions: readonly SitePolicyRevisionRow[]
): Record<string, RevisionSignatures> => {
  const ascending = revisions
    .filter((revision) => revision.status !== PolicyRevisionStatus.CANCELLED)
    .sort((left, right) => left.revisionNumber - right.revisionNumber)
  return Object.fromEntries(
    ascending
      .slice(1)
      .map((revision, index) => [
        revision.revisionId,
        revision.acceptanceGeneration > (ascending[index]?.acceptanceGeneration ?? 0)
          ? RevisionSignatures.SIGN_AGAIN
          : RevisionSignatures.CARRY_OVER,
      ])
  )
}

export const showsSignaturePlaceholderHelp = (
  acceptanceMethod: PolicyAcceptanceMethod | undefined
): boolean => acceptanceMethod === PolicyAcceptanceMethod.SIGNATURE

export type PublishOutcome = {
  revisionNumber: number
  effectiveFrom: string | null
  impactCount: number | null
  raisesGeneration: boolean
  previousRevisionNumber: number | null
}

export const buildOutcomeFromPublish = (
  result: SitePolicyPublishResult,
  previousActive: SitePolicyRevisionRow | null
): PublishOutcome => ({
  revisionNumber: result.revision.revisionNumber,
  effectiveFrom: result.revision.effectiveFrom,
  impactCount: result.impactCount,
  raisesGeneration:
    previousActive === null ||
    result.revision.acceptanceGeneration > previousActive.acceptanceGeneration,
  previousRevisionNumber: previousActive?.revisionNumber ?? null,
})

export const buildOutcomeFromRevisions = (
  revisions: readonly SitePolicyRevisionRow[],
  previousActive: SitePolicyRevisionRow | null
): PublishOutcome | null => {
  const latest = selectRevisionTimeline(revisions.filter(isRevisionLive)).revisions[0]
  if (!latest || (previousActive && latest.revisionNumber <= previousActive.revisionNumber)) {
    return null
  }
  const raisesGeneration =
    previousActive === null || latest.acceptanceGeneration > previousActive.acceptanceGeneration
  return {
    revisionNumber: latest.revisionNumber,
    effectiveFrom: latest.effectiveFrom,
    impactCount: raisesGeneration ? null : 0,
    raisesGeneration,
    previousRevisionNumber: previousActive?.revisionNumber ?? null,
  }
}

export type CopyDescriptor = { key: string; params: Record<string, string | number> }

export const describePublishOutcome = (
  outcome: PublishOutcome,
  formatDate: (isoDate: string) => string
): CopyDescriptor[] => {
  const lines: CopyDescriptor[] = []
  if (outcome.effectiveFrom) {
    lines.push({
      key: `${POLICY_GATING_I18N}.outcome.scheduled`,
      params: {
        revision: outcome.revisionNumber,
        date: formatDate(outcome.effectiveFrom),
      },
    })
    if (outcome.previousRevisionNumber !== null) {
      lines.push({
        key: `${POLICY_GATING_I18N}.outcome.grace`,
        params: {
          previous: outcome.previousRevisionNumber,
          date: formatDate(outcome.effectiveFrom),
        },
      })
    }
  } else {
    lines.push({
      key: `${POLICY_GATING_I18N}.outcome.published`,
      params: { revision: outcome.revisionNumber },
    })
  }
  if (outcome.impactCount !== null) {
    lines.push({
      key: `${POLICY_GATING_I18N}.outcome.impactCount`,
      params: { count: outcome.impactCount },
    })
  } else {
    lines.push({ key: `${POLICY_GATING_I18N}.outcome.impactUnknown`, params: {} })
  }
  return lines
}
